/* UniCurt preview-only optional photo service. Version: uc-photo-approval-v1-20261030.
   No form submission, price override, checkout, order or fulfillment operations. */
(() => {
  'use strict';
  const THEME = '188133015737';
  const VARIANT = '68089385025721';
  const HANDLE = 'pre-shipment-photo-approval';
  const PENDING_KEY = 'uc-photo-approval-pending-v1';
  if (String(window.Shopify?.theme?.id) !== THEME || customElements.get('uc-photo-approval')) return;
  let busy = false;
  const instances = () => [...document.querySelectorAll('uc-photo-approval')];
  const root = window.Shopify.routes?.root || '/';
  const url = path => new URL(root + path, location.origin);
  const pending = () => { try { return sessionStorage.getItem(PENDING_KEY) === '1'; } catch (_) { return false; } };
  const markPending = value => { try { value ? sessionStorage.setItem(PENDING_KEY, '1') : sessionStorage.removeItem(PENDING_KEY); } catch (_) {} };
  async function jsonFetch(target, options = {}) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(target, { credentials:'same-origin', cache:'no-store', ...options, signal:controller.signal });
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        const error = new Error(typeof data.description === 'string' ? data.description : 'Unable to contact the cart. Please try again.');
        // Only a documented add validation rejection is safe to treat as not added.
        error.rejected = response.status === 422;
        throw error;
      }
      return data;
    } finally { clearTimeout(timer); }
  }
  const getCart = () => jsonFetch(url('cart.js'));
  const serviceLines = cart => {
    if (!Array.isArray(cart?.items)) throw new Error('Unable to verify your cart. Please open your cart.');
    return cart.items.filter(item => String(item.variant_id || item.id) === VARIANT);
  };
  function applyCart(cart) {
    const added = serviceLines(cart).length > 0;
    if (added) markPending(false);
    instances().forEach(node => { node.added = added; node.sync(); });
    return added;
  }
  const setBusy = value => { busy = value; instances().forEach(node => node.sync()); };
  function drawerSections(drawer) {
    try {
      const sections = drawer?.getSectionsToRender?.();
      return Array.isArray(sections) && sections.length <= 5 ? sections : [];
    } catch (_) { return []; }
  }
  function validSections(sections, descriptors) {
    return !!sections && descriptors.length > 0 && descriptors.every(section => {
      const html = sections[section.id];
      return typeof html === 'string' && !!new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section');
    });
  }
  async function renderDrawer(payload, trigger) {
    const drawer = document.querySelector('cart-drawer');
    const descriptors = drawerSections(drawer);
    if (!drawer || !descriptors.length || typeof drawer.renderContents !== 'function') return false;
    let sections = payload?.sections;
    if (!validSections(sections, descriptors)) {
      // A failed section render does not mean the add failed. GET only, never resend POST.
      const sectionURL = new URL(location.pathname, location.origin);
      sectionURL.searchParams.set('preview_theme_id', THEME);
      sectionURL.searchParams.set('sections', descriptors.map(section => section.id).join(','));
      try { sections = await jsonFetch(sectionURL); } catch (_) { return false; }
    }
    if (!validSections(sections, descriptors)) return false;
    try {
      drawer.setActiveElement?.(trigger);
      drawer.renderContents({ ...(payload || {}), id:Number(VARIANT), sections });
      return true;
    } catch (_) { return false; }
  }
  function notify(cart) {
    // Keep the theme's existing cart UI subscribers informed without changing their code.
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source:'uc-photo-approval', productVariantId:VARIANT, cartData:cart });
      }
    } catch (_) {}
  }
  class PhotoApproval extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.button = this.querySelector('[data-photo-add]');
      this.label = this.querySelector('[data-photo-label]');
      this.status = this.querySelector('[data-photo-status]');
      this.cartLink = this.querySelector('[data-photo-cart]');
      if (!this.button || !this.label || !this.status || !this.cartLink) return;
      this.defaultLabel = this.defaultLabel || this.label.textContent.trim();
      this.valid = this.dataset.variantId === VARIANT && this.dataset.productHandle === HANDLE && this.dataset.available === 'true';
      this.added = this.dataset.inCart === 'true';
      this.checking = false;
      this.onButtonClick = event => { event.preventDefault(); this.handleClick(); };
      this.button.addEventListener('click', this.onButtonClick);
      this.sync();
      if (!this.valid) { this.message('Photo approval is temporarily unavailable. Please contact us.'); return; }
      // Read-only initial reconciliation. Never add anything on page load.
      this.refresh();
      this.onFocus = () => { if (!busy) this.refresh(); };
      window.addEventListener('focus', this.onFocus);
      window.addEventListener('pageshow', this.onFocus);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => { if (!busy && event?.source !== 'uc-photo-approval') this.refresh(); });
      }
    }
    disconnectedCallback() {
      window.removeEventListener('focus', this.onFocus);
      window.removeEventListener('pageshow', this.onFocus);
      this.unsubscribe?.();
      this.button?.removeEventListener('click', this.onButtonClick);
      this.bound = false;
    }
    message(text, showCart = false) {
      this.status.textContent = text;
      this.status.hidden = !text;
      this.cartLink.hidden = !showCart;
    }
    sync() {
      if (!this.button) return;
      this.button.disabled = !this.valid || busy || this.checking;
      this.button.setAttribute('aria-busy', String(busy || this.checking));
      this.label.textContent = busy ? 'Updating cart…' : this.checking ? 'Checking cart…' : this.added ? 'Photo approval added — View cart' : pending() ? 'Check photo approval in cart' : this.defaultLabel;
    }
    async refresh() {
      if (busy || this.checking) return;
      this.checking = true; this.sync();
      try {
        const cart = await getCart();
        if (this.isConnected && !busy) {
          applyCart(cart);
          if (pending() && !this.added) this.message('An earlier request is unconfirmed. Please check your cart before trying again.', true);
        }
      } catch (_) { /* Click performs a fresh verification; do not silently assume an empty cart. */ }
      finally { this.checking = false; this.sync(); }
    }
    async handleClick() {
      if (!this.valid || busy || this.checking) return;
      setBusy(true); this.message('');
      const operation = async () => {
        const before = await getCart();
        if (applyCart(before)) {
          this.message('Photo approval is already in your cart. No additional service was added.', true);
          await renderDrawer(null, this.button);
          return;
        }
        if (pending()) {
          this.message('We could not confirm an earlier request. Check your cart; no second add has been sent.', true);
          return;
        }
        const product = await jsonFetch(url('products/' + HANDLE + '.js'));
        const variant = product.variants?.find(item => String(item.id) === VARIANT);
        if (!variant?.available) throw new Error('Photo approval is temporarily unavailable. Please try later.');
        if (before.currency !== this.dataset.currency || Number(variant.price) !== Number(this.dataset.price) || (before.currency === 'USD' && Number(variant.price) !== 499)) {
          throw new Error('The price or currency has changed. Refresh this page before adding the service.');
        }
        const drawer = document.querySelector('cart-drawer'), descriptors = drawerSections(drawer);
        const body = { items:[{ id:Number(VARIANT), quantity:1, properties:{ 'Service':'Photos for approval before shipping', '_uc_service':'pre-shipment-photo-approval' } }] };
        if (descriptors.length) {
          body.sections = descriptors.map(section => section.id);
          body.sections_url = location.pathname + '?preview_theme_id=' + THEME;
        }
        markPending(true);
        let payload;
        try {
          payload = await jsonFetch(url('cart/add.js'), { method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'}, body:JSON.stringify(body) });
        } catch (error) {
          if (error.rejected) { markPending(false); throw error; }
          // The POST may have succeeded despite a network/JSON error. Reconcile, never retry it.
          try {
            const after = await getCart();
            if (applyCart(after)) {
              this.message('Photo approval is in your cart. No duplicate request was sent.', true);
              notify(after); await renderDrawer(null, this.button); return;
            }
          } catch (_) {}
          this.message('We could not confirm the add. Please check your cart. No second add was sent.', true);
          return;
        }
        // A valid successful add response proves the service was added even if a later GET fails.
        const returned = payload.items || [payload];
        const accepted = returned.some(item => String(item.variant_id || item.id) === VARIANT && Number(item.quantity) > 0);
        let after = null;
        try { after = await getCart(); } catch (_) {}
        if (after && applyCart(after)) {
          this.message('Photo approval added to your cart.', true);
        } else if (accepted) {
          markPending(false);
          instances().forEach(node => { node.added = true; node.sync(); });
          this.message('Photo approval added. Open your cart to review it.', true);
        } else {
          this.message('We could not verify the cart response. Please check your cart. No second add was sent.', true);
          return;
        }
        if (after) notify(after);
        await renderDrawer(payload, this.button);
      };
      try {
        // Serialize this service across tabs where Web Locks is supported.
        if (navigator.locks?.request) await navigator.locks.request('unicurt-photo-approval-add', operation);
        else await operation();
      } catch (error) { this.message(error.message || 'Unable to add the service. Please check your cart.', true); }
      finally { setBusy(false); }
    }
  }
  customElements.define('uc-photo-approval', PhotoApproval);
})();
